import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ACCOUNT_CHANGED_EVENT, SETTINGS_CHANGED_EVENT } from '../auth/accountScope';
import { loadUserUiSettings, saveUserUiSettings } from '../auth/userSettings';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyThemeClass(theme: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const initial = loadUserUiSettings().theme;
    applyThemeClass(initial);
    return initial;
  });

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  useEffect(() => {
    const refresh = () => {
      const next = loadUserUiSettings().theme;
      setThemeState(next);
      applyThemeClass(next);
    };
    window.addEventListener(ACCOUNT_CHANGED_EVENT, refresh);
    window.addEventListener(SETTINGS_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(ACCOUNT_CHANGED_EVENT, refresh);
      window.removeEventListener(SETTINGS_CHANGED_EVENT, refresh);
    };
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme: (next: ThemeMode) => {
        setThemeState(next);
        saveUserUiSettings({ theme: next });
      },
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
